/**
 * MobileNav.tsx
 *
 * Mobile navigation overlay for EMS.
 * Appears as a hamburger menu on mobile devices.
 */

import { Fragment } from 'react';
import { NavLink } from 'react-router-dom';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import {
  X,
  Menu,
  LayoutDashboard,
  Users,
  Settings,
  Shield,
  Database,
  Activity,
  BarChart3,
  FileText,
  Puzzle,
  Webhook,
  Terminal,
  Lock,
  Globe,
  Zap,
  Layers,
} from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

interface MobileNavProps {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

interface NavSection {
  title: string;
  items: {
    to: string;
    icon: React.ComponentType<{ size?: number }>;
    label: string;
  }[];
}

// ============================================================================
// Navigation Configuration
// ============================================================================

const sections: NavSection[] = [
  {
    title: 'Overview',
    items: [
      { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/activity', icon: Activity, label: 'Activity Feed' },
    ],
  },
  {
    title: 'Users & Access',
    items: [
      { to: '/users', icon: Users, label: 'User Management' },
      { to: '/roles', icon: Shield, label: 'Roles & Groups' },
      { to: '/permissions', icon: Lock, label: 'Permissions' },
    ],
  },
  {
    title: 'Configuration',
    items: [
      { to: '/config/modules', icon: Puzzle, label: 'Modules' },
      { to: '/config/features', icon: Zap, label: 'Feature Flags' },
      { to: '/config/settings', icon: Settings, label: 'System Settings' },
      { to: '/config/branding', icon: Globe, label: 'Branding' },
    ],
  },
  {
    title: 'System',
    items: [
      { to: '/system/health', icon: Activity, label: 'System Health' },
      { to: '/system/database', icon: Database, label: 'Database' },
      { to: '/system/services', icon: Layers, label: 'Services' },
      { to: '/system/logs', icon: Terminal, label: 'Logs' },
    ],
  },
  {
    title: 'Analytics',
    items: [
      { to: '/analytics', icon: BarChart3, label: 'Dashboard' },
      { to: '/analytics/reports', icon: FileText, label: 'Reports' },
    ],
  },
  {
    title: 'Integrations',
    items: [
      { to: '/integrations', icon: Webhook, label: 'Connections' },
      { to: '/integrations/api', icon: Terminal, label: 'API Keys' },
    ],
  },
];

// ============================================================================
// Component
// ============================================================================

export function MobileNav({ isOpen, onOpen, onClose }: MobileNavProps) {
  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={onOpen}
        className="
          fixed top-4 left-4 z-50 lg:hidden
          p-2 rounded-lg
          bg-[var(--grok-surface-1)] border border-[var(--grok-border)]
          text-[var(--grok-text-primary)]
          hover:bg-[var(--grok-hover-bg)]
          focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ems-primary-ring)]
        "
        aria-label="Open menu"
      >
        <Menu size={24} />
      </button>

      {/* Mobile Navigation Drawer */}
      <Transition show={isOpen} as={Fragment}>
        <Dialog onClose={onClose} className="relative z-50 lg:hidden">
          {/* Backdrop */}
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          </TransitionChild>

          {/* Panel */}
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="ease-in duration-200"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <DialogPanel className="fixed inset-y-0 left-0 w-80 bg-[var(--grok-surface-1)] shadow-xl overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-[var(--grok-border)]">
                <div className="flex items-center gap-3">
                  <div
                    className="
                      w-9 h-9 rounded-lg
                      flex items-center justify-center
                      bg-[var(--ems-primary)] text-white
                      font-bold text-base
                    "
                  >
                    E
                  </div>
                  <div>
                    <h1 className="text-base font-semibold text-[var(--grok-text-primary)]">
                      EMS
                    </h1>
                    <p className="text-xs text-[var(--grok-text-muted)]">Admin Console</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="
                    p-2 rounded-lg
                    text-[var(--grok-text-secondary)]
                    hover:bg-[var(--grok-hover-bg)] hover:text-[var(--grok-text-primary)]
                    focus:outline-none
                  "
                  aria-label="Close menu"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Navigation */}
              <nav className="p-4 space-y-6">
                {sections.map((section) => (
                  <div key={section.title}>
                    <h3 className="px-3 text-xs font-semibold text-[var(--grok-text-muted)] uppercase tracking-wider mb-2">
                      {section.title}
                    </h3>
                    <div className="space-y-1">
                      {section.items.map(({ to, icon: Icon, label }) => (
                        <NavLink
                          key={to}
                          to={to}
                          onClick={onClose}
                          className={({ isActive }) => `
                            flex items-center gap-3 px-3 py-2.5 rounded-lg
                            transition-all duration-150
                            ${
                              isActive
                                ? 'bg-[var(--ems-primary-muted)] text-[var(--ems-primary)]'
                                : 'text-[var(--grok-text-secondary)] hover:bg-[var(--grok-hover-bg)] hover:text-[var(--grok-text-primary)]'
                            }
                          `}
                        >
                          <Icon size={20} />
                          <span className="font-medium">{label}</span>
                        </NavLink>
                      ))}
                    </div>
                  </div>
                ))}
              </nav>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>
    </>
  );
}

export default MobileNav;
